import React, { useEffect, useState } from "react";
import { GraduationCap } from "lucide-react";
import api from "../lib/api";
import * as C from "../lib/crypto";
import { REACTIONS } from "../lib/learn";
import { CertificateSetting } from "./Certificate";

const rLabel = (k) => (REACTIONS.find(([v]) => v === k) || [])[1] || k;

const Note = ({ n, beaconKey }) => {
  const [text, setText] = useState(n.note_iv ? null : n.note);
  useEffect(() => {
    if (!n.note_iv || !beaconKey) return;
    C.decryptText(beaconKey, n.note, n.note_iv).then(setText).catch(() => setText(null));
  }, [n.note, n.note_iv, beaconKey]);
  return (
    <li className="font-body text-xs text-zinc-300 border-l-2 border-white/10 pl-2" data-testid="insights-note">
      <span className="whitespace-pre-wrap">{text ?? (beaconKey ? "Can't read this note." : "Unlock your vault to read this note.")}</span>
      <span className="block text-[10px] text-zinc-500 mt-0.5">{n.name || "Anonymous"}{n.reaction ? ` · ${rLabel(n.reaction)}` : ""}</span>
    </li>
  );
};

const Lesson = ({ l, students, beaconKey }) => (
  <div className="py-2.5 border-t border-white/5" data-testid={`insights-lesson-${l.id}`}>
    <div className="flex items-center gap-3">
      <span className="flex-1 font-body text-sm text-white truncate">{l.title}</span>
      <span className="font-body text-xs text-zinc-400" data-testid={`insights-done-${l.id}`}>{l.done} finished</span>
    </div>
    <div className="h-1 bg-white/10 mt-1.5"><div className="h-full bg-[#14A89A]" style={{ width: `${students ? (l.done / students) * 100 : 0}%` }} /></div>
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {l.stopped_here > 0 && <span className="font-body text-[11px] text-amber-400/90" data-testid={`insights-stop-${l.id}`}>{l.stopped_here} stopped after this lesson</span>}
      {Object.entries(l.reactions).map(([k, v]) => <span key={k} className="font-body text-[11px] bg-white/5 border border-white/10 px-1.5 py-0.5 text-zinc-300">{rLabel(k)} {v}</span>)}
    </div>
    {l.names.length > 0 && <div className="font-body text-[11px] text-zinc-500 mt-1">Shared by: {l.names.join(", ")}</div>}
    {l.notes.length > 0 && <ul className="mt-2 space-y-1.5">{l.notes.map((n, i) => <Note key={i} n={n} beaconKey={beaconKey} />)}</ul>}
  </div>
);

// Optional progress tracking. Off: no Mark complete, progress bars, Continue or feedback for students.
export const TeacherInsights = ({ beacon, beaconKey, onChange }) => {
  const on = !!beacon.learning_enabled;
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { await api.put("/beacon/mine", { learning_enabled: !on }); await onChange(); } catch { window.alert("Couldn't save, please try again."); } finally { setBusy(false); }
  };
  return (
    <div className="border border-white/10 bg-[#121212] p-5" data-testid="teacher-insights">
      <div className="flex items-start gap-3">
        <h2 className="flex-1 font-body text-lg text-white flex items-center gap-2"><GraduationCap size={17} className="text-[#14A89A]" /> Progress tracking</h2>
        <button type="button" role="switch" aria-checked={on} onClick={toggle} disabled={busy} data-testid="learning-toggle"
          className={`shrink-0 w-11 h-6 rounded-full relative transition-colors disabled:opacity-50 ${on ? "bg-[#14A89A]" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 left-0 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>
      <p className="font-body text-xs text-zinc-500 mt-1 mb-3">{on
        ? "On. Students can mark lessons complete, see their progress, pick up where they left off and leave optional feedback. You see counts below."
        : "Off. Turn it on to let students mark lessons complete, see their progress and leave optional feedback, with learning insights for you here. Your lessons work the same either way."}</p>
      {on && <InsightsBody beaconKey={beaconKey} />}
      <BlanksToggle beacon={beacon} onChange={onChange} />
      <CertificateSetting beacon={beacon} onChange={onChange} Switch={SettingSwitch} />
    </div>
  );
};

const SettingSwitch = ({ on, field, testid, onChange }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { await api.put("/beacon/mine", { [field]: !on }); await onChange(); } catch { window.alert("Couldn't save, please try again."); } finally { setBusy(false); }
  };
  return (
    <button type="button" role="switch" aria-checked={on} onClick={toggle} disabled={busy} data-testid={testid}
      className={`shrink-0 w-11 h-6 rounded-full relative transition-colors disabled:opacity-50 ${on ? "bg-[#14A89A]" : "bg-white/15"}`}>
      <span className={`absolute top-0.5 left-0 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
};

const BlanksToggle = ({ beacon, onChange }) => {
  const on = beacon.notes_blanks !== false;
  return (
    <div className="mt-4 pt-4 border-t border-white/10 flex items-start gap-3" data-testid="notes-blanks-setting">
      <div className="flex-1">
        <div className="font-body text-sm text-white">Study sheets list lessons without notes</div>
        <p className="font-body text-xs text-zinc-500 mt-0.5">Students can download their private notes from a course as a study sheet. {on ? "On: every lesson is listed, with space for the ones they haven't written on yet." : "Off: only lessons with notes are listed."} Students can still change this when they download. You never see their notes.</p>
      </div>
      <SettingSwitch on={on} field="notes_blanks" testid="notes-blanks-toggle" onChange={onChange} />
    </div>
  );
};

const InsightsBody = ({ beaconKey }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/beacon/mine/learning").then((r) => setD(r.data)).catch(() => setD({ students: 0, courses: [] })); }, []);
  if (!d) return null;
  const courses = d.courses.filter((c) => c.lessons.length);
  return (
    <div data-testid="learning-insights">
      <p className="font-body text-xs text-zinc-500 mb-3">Counts only. Names appear only for students who choose to share them, and feedback is anonymous unless a student adds their name.</p>
      {!d.students ? <p className="font-body text-sm text-zinc-400" data-testid="insights-empty">Insights appear once students start marking lessons complete.</p>
        : courses.map((c) => (
          <details key={c.id} className="mb-2 border border-white/10 px-3 py-2" data-testid={`insights-course-${c.id}`}>
            <summary className="cursor-pointer font-body text-sm text-white">{c.title} <span className="text-zinc-500 text-xs ml-2">{c.students} learning · {c.finished} finished</span></summary>
            {c.lessons.map((l) => <Lesson key={l.id} l={l} students={c.students} beaconKey={beaconKey} />)}
          </details>
        ))}
    </div>
  );
};
